import {useState} from 'react'
import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
  type NavigationProp,
} from '#/lib/routes/types'
import {useContextEngine} from '#/state/contextEngine/ContextEngineProvider'
import * as SettingsList from '#/screens/Settings/components/SettingsList'
import {PhotoContextSection} from '#/screens/Settings/ContextEngine/PhotoContextSection'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as TextField from '#/components/forms/TextField'
import {CircleInfo_Stroke2_Corner0_Rounded as InfoIcon} from '#/components/icons/CircleInfo'
import {Trash_Stroke2_Corner0_Rounded as TrashIcon} from '#/components/icons/Trash'
import * as Layout from '#/components/Layout'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {IS_NATIVE} from '#/env'

type Props = NativeStackScreenProps<
  CommonNavigatorParams,
  'ContextEngineSettings'
>

/**
 * Context Engine (Phase 1, location-only) — OPT-IN, OFF by default. Clear privacy
 * copy, a visible active indicator, and one-tap off. Location permission is only
 * requested when the user opts in.
 */
export function ContextEngineSettingsScreen({}: Props) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const navigation = useNavigation<NavigationProp>()
  const {
    prefs,
    active,
    permissionGranted,
    setEnabled,
    setHome,
    setWork,
    addLabeledPlace,
    deleteLabeledPlace,
    backgroundSupported,
    backgroundActive,
    backgroundPermissionGranted,
    setBackgroundEnabled,
  } = useContextEngine()

  const [placeName, setPlaceName] = useState('')
  // Capturing the current location is async (and can fail/be denied), so reflect a
  // "saving…" state and confirm the outcome with a toast — the old path gave no feedback.
  const [saving, setSaving] = useState(false)
  const places = prefs.places ?? []
  const onAddPlace = async () => {
    const name = placeName.trim()
    if (!name || saving) return
    setSaving(true)
    const res = await addLabeledPlace(name)
    setSaving(false)
    if (res.ok) {
      setPlaceName('') // clear the field so the new row is the visible confirmation
      Toast.show(l`Saved “${res.name}” at your current location.`)
    } else if (res.reason === 'permission') {
      Toast.show(l`Location permission is needed to save a place.`, {
        type: 'warning',
      })
    } else if (res.reason === 'unsupported') {
      Toast.show(l`Saving a place is only available on the mobile app.`, {
        type: 'warning',
      })
    } else if (res.reason === 'location') {
      Toast.show(l`Could not read your location. Please try again.`, {
        type: 'error',
      })
    }
  }

  return (
    <Layout.Screen>
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Context Engine</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>

      <Layout.Content>
        <View style={[a.p_lg, a.gap_lg]}>
          {/* Active indicator */}
          <View
            style={[
              a.flex_row,
              a.align_center,
              a.gap_sm,
              a.rounded_md,
              a.p_md,
              active
                ? {backgroundColor: t.palette.positive_50}
                : t.atoms.bg_contrast_25,
            ]}>
            <View
              style={[
                a.rounded_full,
                {
                  width: 10,
                  height: 10,
                  backgroundColor: active
                    ? t.palette.positive_500
                    : t.palette.contrast_400,
                },
              ]}
            />
            <Text style={[a.text_md, a.font_bold, t.atoms.text]}>
              {active ? (
                <Trans>Active — recognizing coarse places</Trans>
              ) : prefs.enabled && !permissionGranted ? (
                <Trans>On, but location permission is needed</Trans>
              ) : (
                <Trans>Off</Trans>
              )}
            </Text>
          </View>

          {/* Privacy copy */}
          <View style={[a.flex_row, a.gap_sm]}>
            <InfoIcon size="sm" fill={t.atoms.text_contrast_medium.color} />
            <Text
              style={[
                a.flex_1,
                a.text_sm,
                a.leading_snug,
                t.atoms.text_contrast_medium,
              ]}>
              <Trans>
                Phase 1 uses your location ONLY, only while the app is open. It
                runs on-device and stores CONCLUSIONS (like “home”, “a venue”,
                or how long you stayed) — never your raw location, and never
                audio, microphone, or camera. You can view and delete everything
                below.
              </Trans>
            </Text>
          </View>

          {/* One-tap on/off */}
          <Button
            label={
              prefs.enabled
                ? 'Turn off Context Engine'
                : 'Turn on Context Engine'
            }
            size="large"
            variant="solid"
            color={prefs.enabled ? 'secondary' : 'primary'}
            onPress={() => setEnabled(!prefs.enabled)}>
            <ButtonText>
              {prefs.enabled ? <Trans>Turn off</Trans> : <Trans>Turn on</Trans>}
            </ButtonText>
          </Button>
          {prefs.enabled && !permissionGranted && IS_NATIVE ? (
            <Button
              label="Grant location permission"
              size="small"
              variant="outline"
              color="primary"
              onPress={() => setEnabled(true)}>
              <ButtonText>
                <Trans>Grant location permission</Trans>
              </ButtonText>
            </Button>
          ) : null}

          {/* ── Phase 1.5: all-day BACKGROUND place context — a SEPARATE, higher
              opt-in. OFF by default; independent of the when-in-use toggle above. ── */}
          {backgroundSupported ? (
            <View
              style={[
                a.gap_md,
                a.pt_lg,
                a.mt_sm,
                a.border_t,
                t.atoms.border_contrast_low,
              ]}>
              <Text style={[a.text_md, a.font_bold, t.atoms.text]}>
                <Trans>All-day place context</Trans>
              </Text>

              {/* Background active indicator */}
              <View
                style={[
                  a.flex_row,
                  a.align_center,
                  a.gap_sm,
                  a.rounded_md,
                  a.p_md,
                  backgroundActive
                    ? {backgroundColor: t.palette.positive_50}
                    : t.atoms.bg_contrast_25,
                ]}>
                <View
                  style={[
                    a.rounded_full,
                    {
                      width: 10,
                      height: 10,
                      backgroundColor: backgroundActive
                        ? t.palette.positive_500
                        : t.palette.contrast_400,
                    },
                  ]}
                />
                <Text style={[a.text_md, a.font_bold, t.atoms.text]}>
                  {backgroundActive ? (
                    <Trans>
                      On — recognizing places all day in the background
                    </Trans>
                  ) : prefs.backgroundEnabled &&
                    !backgroundPermissionGranted ? (
                    <Trans>
                      On, but “Always” location permission is needed
                    </Trans>
                  ) : (
                    <Trans>Off</Trans>
                  )}
                </Text>
              </View>

              {/* Transparency copy for the higher opt-in */}
              <View style={[a.flex_row, a.gap_sm]}>
                <InfoIcon size="sm" fill={t.atoms.text_contrast_medium.color} />
                <Text
                  style={[
                    a.flex_1,
                    a.text_sm,
                    a.leading_snug,
                    t.atoms.text_contrast_medium,
                  ]}>
                  <Trans>
                    This goes further than Phase 1: it uses “Always” location so
                    it can notice the places you go even when the app is closed.
                    It still runs battery-light, still stores ONLY conclusions
                    (place + how long) — never a GPS trail — and still no audio,
                    microphone, or camera. Leave it off to keep location to
                    when-the-app-is-open only.
                  </Trans>
                </Text>
              </View>

              {/* Distinct background on/off */}
              <Button
                label={
                  prefs.backgroundEnabled
                    ? 'Turn off all-day place context'
                    : 'Turn on all-day place context'
                }
                size="large"
                variant="solid"
                color={prefs.backgroundEnabled ? 'secondary' : 'primary'}
                onPress={() => setBackgroundEnabled(!prefs.backgroundEnabled)}>
                <ButtonText>
                  {prefs.backgroundEnabled ? (
                    <Trans>Turn off all-day context</Trans>
                  ) : (
                    <Trans>Turn on all-day context</Trans>
                  )}
                </ButtonText>
              </Button>
              {prefs.backgroundEnabled &&
              !backgroundPermissionGranted &&
              IS_NATIVE ? (
                <Button
                  label="Grant Always location permission"
                  size="small"
                  variant="outline"
                  color="primary"
                  onPress={() => setBackgroundEnabled(true)}>
                  <ButtonText>
                    <Trans>Grant “Always” location permission</Trans>
                  </ButtonText>
                </Button>
              ) : null}
            </View>
          ) : null}

          {/* ── Photo context — a SEPARATE, independently-toggleable capability,
              consolidated into this hub so all context options live in one place.
              Metadata-only; opt-in state is preserved (shared photo store). ── */}
          <View
            style={[
              a.gap_md,
              a.pt_lg,
              a.mt_sm,
              a.border_t,
              t.atoms.border_contrast_low,
            ]}>
            <Text style={[a.text_md, a.font_bold, t.atoms.text]}>
              <Trans>Photo context</Trans>
            </Text>
            <PhotoContextSection />
          </View>
        </View>

        <SettingsList.Container>
          <SettingsList.Group iconInset={false}>
            <SettingsList.ItemText>
              <Trans>Anchors (stored on-device only)</Trans>
            </SettingsList.ItemText>
            <View style={[a.flex_row, a.gap_sm, a.pt_xs]}>
              <Button
                label="Set current location as Home"
                size="small"
                variant="solid"
                color="secondary"
                disabled={!IS_NATIVE}
                onPress={() => setHome()}>
                <ButtonText>
                  {prefs.home ? (
                    <Trans>Update Home</Trans>
                  ) : (
                    <Trans>Set Home</Trans>
                  )}
                </ButtonText>
              </Button>
              <Button
                label="Set current location as Work"
                size="small"
                variant="solid"
                color="secondary"
                disabled={!IS_NATIVE}
                onPress={() => setWork()}>
                <ButtonText>
                  {prefs.work ? (
                    <Trans>Update Work</Trans>
                  ) : (
                    <Trans>Set Work</Trans>
                  )}
                </ButtonText>
              </Button>
            </View>
            <Text style={[a.text_xs, t.atoms.text_contrast_medium, a.pt_xs]}>
              {prefs.home ? '✓ Home set · ' : 'Home not set · '}
              {prefs.work ? '✓ Work set' : 'Work not set'}
            </Text>
          </SettingsList.Group>

          <SettingsList.Group iconInset={false}>
            <SettingsList.ItemText>
              <Trans>Saved places (stored on-device only)</Trans>
            </SettingsList.ItemText>
            <Text style={[a.text_xs, t.atoms.text_contrast_medium, a.pt_2xs]}>
              <Trans>
                Name where you are now (Home, School, Sports Practice). One is
                recognized by proximity and labeled in your context —
                coordinates stay on this device.
              </Trans>
            </Text>
            <View style={[a.flex_row, a.gap_sm, a.pt_xs]}>
              <View style={[a.flex_1]}>
                <TextField.Root>
                  <TextField.Input
                    label={l`Place name`}
                    placeholder={l`e.g. Sports Practice`}
                    value={placeName}
                    onChangeText={setPlaceName}
                    onSubmitEditing={() => {
                      void onAddPlace()
                    }}
                  />
                </TextField.Root>
              </View>
              <Button
                label={l`Save current location as this place`}
                size="small"
                variant="solid"
                color="primary"
                disabled={!IS_NATIVE || !placeName.trim() || saving}
                onPress={() => {
                  void onAddPlace()
                }}>
                <ButtonText>
                  {saving ? <Trans>Saving…</Trans> : <Trans>Save here</Trans>}
                </ButtonText>
              </Button>
            </View>
            {places.length === 0 ? (
              <Text style={[a.text_xs, t.atoms.text_contrast_low, a.pt_xs]}>
                <Trans>No saved places yet.</Trans>
              </Text>
            ) : (
              <View style={[a.gap_2xs, a.pt_sm]}>
                {places.map(p => (
                  <View
                    key={p.id}
                    style={[a.flex_row, a.align_center, a.gap_sm, a.py_2xs]}>
                    <View style={[a.flex_1, a.gap_2xs]}>
                      <Text
                        emoji
                        style={[a.text_md, a.font_bold, t.atoms.text]}
                        numberOfLines={1}>
                        {p.name || l`Unnamed place`}
                      </Text>
                      <Text
                        style={[a.text_xs, t.atoms.text_contrast_medium]}
                        numberOfLines={1}>
                        {p.savedAt
                          ? l`Saved ${i18n.date(p.savedAt, {dateStyle: 'medium', timeStyle: 'short'})} · stays on this device`
                          : l`Coordinates stay on this device`}
                      </Text>
                    </View>
                    <Button
                      label={`${l`Delete`} ${p.name || l`place`}`}
                      size="tiny"
                      variant="ghost"
                      color="negative"
                      shape="round"
                      onPress={() => deleteLabeledPlace(p.id)}>
                      <ButtonIcon icon={TrashIcon} />
                    </Button>
                  </View>
                ))}
              </View>
            )}
          </SettingsList.Group>

          <SettingsList.PressableItem
            label="View context log"
            onPress={() => navigation.navigate('ContextLog')}>
            <SettingsList.ItemText>
              <Trans>View context log</Trans>
            </SettingsList.ItemText>
            <SettingsList.Chevron />
          </SettingsList.PressableItem>
        </SettingsList.Container>
      </Layout.Content>
    </Layout.Screen>
  )
}
